import * as React from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  apiClient,
  ApiError,
  PaymentStatus,
  type PaymentRecordInput,
  type Program,
  type ReconcileEnrolment,
} from "@alight/contracts";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@alight/ui";
import {
  CheckCircle2,
  Clock,
  AlertCircle,
  History,
  Loader2,
  Mail,
  MoreHorizontal,
  Search,
  Send,
  X,
} from "lucide-react";
import {
  type FeeCorrection,
  type RequestScope,
  formatAud,
  formatDateTime,
  PaymentHistory,
  RecordPaymentDialog,
  RequestPaymentDialog,
  RECON_STATUS_BADGE,
  RECON_STATUS_LABEL,
} from "../components/ReconcilePayments";
import { RowAction, RowActions } from "../components/RowActions";

/* Reconciliation is the heart of the host workflow: scan the enrolments whose
 * payment needs attention, match the bank transfer (in full or in part), and
 * record it — partial amounts flip the record to `partial`, clearing the
 * balance flips it to `paid` and emails the parent. Each row can also be
 * pushed to the account manager (a magic-link email straight to the record's
 * /reconcile page). Supports a `?ref=` deep-link that highlights a row. */

const FILTERS = [
  { value: "pending_review", label: "Pending review" },
  { value: "partial", label: "Partially paid" },
  { value: "unpaid", label: "Unpaid" },
  { value: "paid", label: "Paid" },
] as const satisfies ReadonlyArray<{ value: PaymentStatus; label: string }>;

/* Same-origin API by default; override for split deployments. Mirrors pay.$ref. */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

/* Business days after which a pending payment is "stale" enough to chase.
 * The live value comes from GET /api/payment-config (host-editable in
 * Settings); this is only the fallback while that request is in flight. */
const STALE_DAYS = Number(import.meta.env.VITE_PAYMENT_CLEAR_DAYS ?? 3);

export interface ReconSearch {
  status: PaymentStatus;
  ref?: string;
}

/** Whole days elapsed since the payment intent was recorded. */
function daysSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / 86_400_000));
}

export function AdminReconciliation() {
  /* `strict: false` because this screen lives in a shared package with no
     route tree of its own; each app declares the matching validateSearch. */
  const search = useSearch({ strict: false }) as ReconSearch;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const client = React.useMemo(() => apiClient(API_BASE_URL), []);

  const list = useQuery({
    queryKey: ["admin", "reconcile", search.status],
    queryFn: () => client.adminReconcileList({ payment_status: search.status }),
  });

  /* EVERYTHING still owing, whatever tab is showing and whatever program is
   * selected — the only honest basis for "the whole family owes $X".
   *
   * The visible list is narrowed twice over (one payment status, one program),
   * so counting siblings from it under-reports: a family with one child unpaid
   * and another part-paid would be offered "1 registration" on the Unpaid tab
   * while the email the server actually sends covers both. The dialog's
   * numbers have to match what gets sent, or the host stops believing them.
   * Unfiltered, this is the same endpoint's default — react-query caches it
   * alongside the tab the host is on. */
  const owingAll = useQuery({
    queryKey: ["admin", "reconcile", "owing-all"],
    queryFn: () => client.adminReconcileList({}),
    staleTime: 30 * 1000,
  });

  /** parent_email → what that family owes between them. */
  const familyOwing = React.useMemo(() => {
    const byParent = new Map<string, { count: number; totalCents: number }>();
    for (const e of owingAll.data ?? []) {
      const key = (e.parent_email ?? "").trim().toLowerCase();
      if (!key) continue;
      const owed = Math.max(e.amount_cents - e.received_cents, 0);
      if (owed <= 0) continue;
      const at = byParent.get(key) ?? { count: 0, totalCents: 0 };
      at.count += 1;
      at.totalCents += owed;
      byParent.set(key, at);
    }
    return byParent;
  }, [owingAll.data]);

  /* Host-configurable clearing window (admin → Settings → Timing). */
  const paymentConfig = useQuery({
    queryKey: ["payment-config"],
    queryFn: () => client.paymentConfig(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const staleDays = paymentConfig.data?.clear_days ?? STALE_DAYS;

  /* Programs feed the always-on program filter and resolve program_id to a
   * readable name. Includes hidden/past programs so old terms stay reachable. */
  const programsQuery = useQuery({
    queryKey: ["admin", "programs"],
    queryFn: () => client.adminListPrograms(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const programs = React.useMemo(
    () =>
      [...(programsQuery.data ?? [])].sort(
        (a, b) => a.sort_order - b.sort_order || b.id - a.id,
      ),
    [programsQuery.data],
  );
  const programById = React.useMemo(() => {
    const m = new Map<string, Program>();
    for (const p of programs) m.set(String(p.id), p);
    return m;
  }, [programs]);
  const programName = React.useCallback(
    (id: string | null | undefined): string | null => {
      if (id === null || id === undefined || id === "") return null;
      return programById.get(id)?.title ?? `Program #${id}`;
    },
    [programById],
  );

  /* The reconcile view is always scoped to ONE program (term). Default to the
   * latest — the first active program in sort order, else the first program.
   * `null` means "not chosen yet"; the effective value resolves once loaded. */
  const [programChoice, setProgramChoice] = React.useState<string | null>(null);
  const latestProgramId = React.useMemo(() => {
    const current = programs.find((p) => p.active === 1) ?? programs[0];
    return current ? String(current.id) : null;
  }, [programs]);
  const programFilter = programChoice ?? latestProgramId;

  const [recordTarget, setRecordTarget] = React.useState<ReconcileEnrolment | null>(null);
  const [historyTarget, setHistoryTarget] = React.useState<ReconcileEnrolment | null>(null);
  /* The envelope is one click away from a real email, and the row it belongs
   * to is easy to lose in a long table — so it asks first, naming the record,
   * and says afterwards which one went. See notifyTarget/notifySent below. */
  const [notifyTarget, setNotifyTarget] = React.useState<ReconcileEnrolment | null>(null);
  const [notifySent, setNotifySent] = React.useState<ReconcileEnrolment | null>(null);
  /* Ask one family for what they still owe, in the host's own words. */
  const [requestTarget, setRequestTarget] = React.useState<ReconcileEnrolment | null>(null);
  const [requestSent, setRequestSent] = React.useState<{
    enrolment: ReconcileEnrolment;
    to: string;
    /** How many registrations that one email actually asked about — 1 for a
     *  single child, more when the family was chased together. */
    covered: number;
  } | null>(null);
  /* A fee corrected on its own, with no email — confirmed on the page, since
     the row it belonged to has usually moved to another tab by then. */
  const [feeSaved, setFeeSaved] = React.useState<{
    enrolment: ReconcileEnrolment;
    before: number;
    after: number;
  } | null>(null);

  const record = useMutation({
    mutationFn: (args: { ref: string; input: PaymentRecordInput }) =>
      client.adminReconcileRecordPayment(args.ref, args.input),
    onSuccess: () => {
      // Recording changes the enrolment's status, so every filter view is stale.
      void queryClient.invalidateQueries({ queryKey: ["admin", "reconcile"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "enrolments"] });
      setRecordTarget(null);
    },
  });

  const notifyAm = useMutation({
    mutationFn: (id: number) => client.notifyAccountManager(id),
    onSuccess: () => {
      setNotifySent(notifyTarget);
      setNotifyTarget(null);
    },
  });

  const requestPayment = useMutation({
    mutationFn: (args: { ref: string; message: string; scope: RequestScope }) =>
      args.scope === "family"
        ? client
            .adminRequestFamilyPayment(args.ref, { message: args.message })
            .then((r) => ({ sent_to: r.sent_to, covered: r.refs.length }))
        : client
            .adminRequestPayment(args.ref, { message: args.message })
            .then((r) => ({ sent_to: r.sent_to, covered: 1 })),
    onSuccess: (result) => {
      if (requestTarget) {
        setRequestSent({
          enrolment: requestTarget,
          to: result.sent_to,
          covered: result.covered,
        });
      }
      setRequestTarget(null);
      // The chase is now in the event history of every record it covered.
      void queryClient.invalidateQueries({ queryKey: ["admin", "reconcile"] });
    },
  });

  /* Correct what a registration is CHARGED — the fix for a waiver or discount
   * that should never have applied. The status follows the corrected fee, so
   * the record leaves "Paid" and becomes chaseable like any other. */
  const adjustFee = useMutation({
    mutationFn: (args: { ref: string; amount_cents: number; reason?: string }) =>
      client.adminReconcileAdjustFee(args.ref, {
        amount_cents: args.amount_cents,
        ...(args.reason ? { reason: args.reason } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "reconcile"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "enrolments"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "invoices"] });
    },
  });

  /* Remove a mistakenly recorded payment — the record's status drops back to
   * partial/unpaid so the correct amount can be recorded again. */
  const voidPayment = useMutation({
    mutationFn: (args: { ref: string; paymentId: number }) =>
      client.adminReconcileVoidPayment(args.ref, args.paymentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "reconcile"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "enrolments"] });
    },
  });

  /* Correct a recorded payment in place — status follows the corrected money
   * in either direction (paid ↔ partially paid ↔ unpaid). */
  const editPayment = useMutation({
    mutationFn: (args: { ref: string; paymentId: number; input: PaymentRecordInput }) =>
      client.adminReconcileUpdatePayment(args.ref, args.paymentId, args.input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "reconcile"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "enrolments"] });
    },
  });

  const openRequest = (e: ReconcileEnrolment) => {
    setRequestSent(null);
    setFeeSaved(null);
    requestPayment.reset();
    adjustFee.reset();
    setRequestTarget(e);
  };

  /* Correct the fee first (when one came back), then ask. In that order: the
     email quotes the balance the server reads off the record, so a fee saved
     after the email would have asked for the old amount. */
  const sendRequest = async (input: {
    message: string;
    fee?: FeeCorrection;
    scope: RequestScope;
  }) => {
    if (!requestTarget) return;
    const ref = requestTarget.payment_ref;
    if (input.fee) {
      try {
        await adjustFee.mutateAsync({ ref, ...input.fee });
      } catch {
        return; // adjustFee.isError renders in the dialog; email nothing
      }
    }
    requestPayment.mutate({ ref, message: input.message, scope: input.scope });
  };

  const saveFeeOnly = async (fee: FeeCorrection) => {
    if (!requestTarget) return;
    const target = requestTarget;
    try {
      await adjustFee.mutateAsync({ ref: target.payment_ref, ...fee });
      setFeeSaved({
        enrolment: target,
        before: target.amount_cents,
        after: fee.amount_cents,
      });
      setRequestTarget(null);
    } catch {
      /* adjustFee.isError renders in the dialog */
    }
  };

  const history = useQuery({
    queryKey: ["admin", "reconcile", "detail", historyTarget?.payment_ref],
    queryFn: () => client.adminReconcileGet(historyTarget!.payment_ref),
    enabled: historyTarget !== null,
  });

  /* Without a route tree in this package, `navigate`'s search reducer resolves
     to the router-wide union rather than this screen's ReconSearch. The app's
     validateSearch is the real contract (see the route file), so the cast is
     asserting what that already guarantees rather than papering over a gap. */
  const updateSearch = (patch: Partial<ReconSearch>) => {
    void navigate({ search: ((prev: ReconSearch) => ({ ...prev, ...patch })) as never });
  };

  const setStatus = (status: PaymentStatus) => updateSearch({ status });

  const clearRef = () => updateSearch({ ref: undefined });

  /* Rows without a program_id (older/manual records) are never hidden — an
   * unpaid enrolment must stay visible no matter which term is selected. */
  const rows = React.useMemo(() => {
    const all = list.data ?? [];
    if (!programFilter) return all;
    return all.filter((e) => !e.program_id || e.program_id === programFilter);
  }, [list.data, programFilter]);
  const deepLinkRef = search.ref;
  const deepLinkMatch = deepLinkRef
    ? rows.some((e) => e.payment_ref === deepLinkRef)
    : false;

  // A ?ref= deep link may target a row in another term — switch the program
  // filter to wherever the reference actually lives so the link always lands.
  React.useEffect(() => {
    if (!deepLinkRef) return;
    const target = (list.data ?? []).find((e) => e.payment_ref === deepLinkRef);
    if (target?.program_id && target.program_id !== programFilter) {
      setProgramChoice(target.program_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkRef, list.data]);

  // When arriving via ?ref=, scroll the highlighted row into view once loaded.
  React.useEffect(() => {
    if (!deepLinkRef || !deepLinkMatch) return;
    const el = document.getElementById(`enrolment-${deepLinkRef}`);
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [deepLinkRef, deepLinkMatch, list.dataUpdatedAt]);

  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h2 className="font-heading text-2xl font-extrabold text-ink">Reconciliation</h2>
        <p className="text-sm text-muted">
          Match incoming transfers to enrolments and record them — in part or in full.
          Allow up to {staleDays} business days for transfers to clear. Chasing a family,
          emailing the account manager and the payment history all live behind the{" "}
          <MoreHorizontal className="inline h-3.5 w-3.5" aria-hidden="true" /> button at the
          end of each row.
        </p>
      </header>

      {/* Status tabs + always-on program filter */}
      <div className="flex flex-wrap items-center gap-3">
        <div
          role="tablist"
          aria-label="Filter by payment status"
          className="flex flex-wrap gap-2"
        >
          {FILTERS.map((f) => {
            const active = search.status === f.value;
            return (
              <Button
                key={f.value}
                role="tab"
                aria-selected={active}
                type="button"
                size="sm"
                variant={active ? "default" : "outline"}
                onClick={() => setStatus(f.value)}
              >
                {f.label}
              </Button>
            );
          })}
          {/* If a deep-link points at a non-tab status (e.g. refunded), reflect it. */}
          {!FILTERS.some((f) => f.value === search.status) && (
            <Badge variant="outline" className="self-center">
              {RECON_STATUS_LABEL[search.status]}
            </Badge>
          )}
        </div>
        {programs.length > 0 && programFilter !== null && (
          <Select value={programFilter} onValueChange={setProgramChoice}>
            <SelectTrigger
              className="w-64 sm:ml-auto"
              aria-label="Filter by program"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {programs.map((p) => (
                <SelectItem key={p.id} value={String(p.id)}>
                  {p.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Deep-link banner */}
      {deepLinkRef && (
        <div
          className="flex flex-wrap items-center gap-3 rounded-card border border-primary/30 bg-primary/5 p-3 text-sm"
          role="status"
        >
          <Search className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <span className="text-ink">
            Highlighting reference <strong>{deepLinkRef}</strong>
            {!list.isLoading && !deepLinkMatch && (
              <span className="text-muted">
                {" "}
                — not in the “{RECON_STATUS_LABEL[search.status]}” list. Try another filter.
              </span>
            )}
          </span>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="ml-auto"
            onClick={clearRef}
          >
            <X className="h-4 w-4" aria-hidden="true" /> Clear
          </Button>
        </div>
      )}

      {/* Notify-AM feedback */}
      {notifyAm.isSuccess && (
        <div className="rounded-card border border-success/30 bg-success/5 p-3 text-sm text-ink" role="status">
          {notifyAm.data.emailed
            ? "Reconcile link emailed to the account manager."
            : "Couldn't email the account manager — check ACCOUNT_MANAGER_EMAILS is configured."}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{RECON_STATUS_LABEL[search.status]} payments</CardTitle>
          <CardDescription>
            {list.isLoading
              ? "Loading…"
              : `${rows.length} enrolment${rows.length === 1 ? "" : "s"}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {feeSaved && (
            <p className="mb-4 flex flex-wrap items-center gap-2 rounded-card border border-border bg-paper p-3 text-sm text-ink">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-success" aria-hidden="true" />
              Fee for <strong>{feeSaved.enrolment.payment_ref}</strong> (
              {feeSaved.enrolment.child_name}) changed from{" "}
              <strong>{formatAud(feeSaved.before)}</strong> to{" "}
              <strong>{formatAud(feeSaved.after)}</strong>
              {feeSaved.after > feeSaved.enrolment.received_cents &&
                " — it's back on the unpaid list."}
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="ml-auto text-xs text-muted"
                onClick={() => setFeeSaved(null)}
              >
                Dismiss
              </Button>
            </p>
          )}
          {requestSent && (
            <p className="mb-4 flex flex-wrap items-center gap-2 rounded-card border border-border bg-paper p-3 text-sm text-ink">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-success" aria-hidden="true" />
              {requestSent.covered > 1 ? (
                <>
                  Payment request emailed to <strong>{requestSent.to}</strong>, covering{" "}
                  <strong>{requestSent.covered} registrations</strong> in one message.
                </>
              ) : (
                <>
                  Payment request emailed to <strong>{requestSent.to}</strong> for{" "}
                  <strong>{requestSent.enrolment.payment_ref}</strong> (
                  {requestSent.enrolment.child_name}).
                </>
              )}
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="ml-auto text-xs text-muted"
                onClick={() => setRequestSent(null)}
              >
                Dismiss
              </Button>
            </p>
          )}
          {notifySent && (
            <p className="mb-4 flex flex-wrap items-center gap-2 rounded-card border border-border bg-paper p-3 text-sm text-ink">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-success" aria-hidden="true" />
              Reconcile link emailed to the account manager for{" "}
              <strong>{notifySent.payment_ref}</strong> — {notifySent.parent_name} (
              {notifySent.child_name}).
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="ml-auto text-xs text-muted"
                onClick={() => setNotifySent(null)}
              >
                Dismiss
              </Button>
            </p>
          )}
          {list.isLoading ? (
            <div className="flex items-center gap-2 py-10 text-muted">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Loading enrolments…
            </div>
          ) : list.isError ? (
            <div
              className="flex items-start gap-3 rounded-card border border-border bg-paper p-4 text-sm"
              role="alert"
            >
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-accent" aria-hidden="true" />
              <div>
                <p className="font-semibold text-ink">Couldn&apos;t load enrolments</p>
                <p className="mt-1 text-muted">
                  {list.error instanceof ApiError
                    ? `The API returned an error (${list.error.status}).`
                    : "Please check your connection and try again."}
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="mt-3"
                  onClick={() => void list.refetch()}
                >
                  Retry
                </Button>
              </div>
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-center">
              <CheckCircle2 className="h-8 w-8 text-success" aria-hidden="true" />
              <p className="font-semibold text-ink">Nothing to reconcile</p>
              <p className="text-sm text-muted">
                No enrolments are currently{" "}
                {RECON_STATUS_LABEL[search.status].toLowerCase()}.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Parent</TableHead>
                  <TableHead>Child</TableHead>
                  <TableHead className="text-right">Fee</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="text-right">Days pending</TableHead>
                  <TableHead className="sticky right-0 z-10 bg-card text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((e) => {
                  const days = daysSince(e.intent_at);
                  const stale = days !== null && days >= staleDays;
                  const highlighted = deepLinkRef === e.payment_ref;
                  return (
                    <TableRow
                      key={e.id}
                      id={`enrolment-${e.payment_ref}`}
                      className={highlighted ? "bg-primary/5" : undefined}
                    >
                      <TableCell className="font-mono font-semibold text-ink">
                        <div className="flex items-center gap-2">
                          {e.payment_ref}
                          {e.payment_status !== search.status && (
                            <Badge variant={RECON_STATUS_BADGE[e.payment_status]}>
                              {RECON_STATUS_LABEL[e.payment_status]}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-ink">{e.parent_name}</div>
                        <a
                          href={`mailto:${e.parent_email}`}
                          className="text-xs text-muted hover:text-primary"
                        >
                          {e.parent_email}
                        </a>
                      </TableCell>
                      <TableCell>
                        <div className="text-ink">{e.child_name}</div>
                        <div className="text-xs text-muted">
                          {e.year_level}
                          {!e.program_id && " · program not recorded"}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium text-ink">
                        {formatAud(e.amount_cents)}
                        {e.received_cents > 0 && e.payment_status !== "paid" && (
                          <div className="text-xs font-normal text-muted">
                            {formatAud(e.received_cents)} received
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-ink">
                        {e.payment_status === "paid" ? (
                          <span className="text-success">—</span>
                        ) : (
                          formatAud(Math.max(e.balance_cents, 0))
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {days === null ? (
                          <span className="text-muted">—</span>
                        ) : (
                          <span
                            className={
                              stale
                                ? "inline-flex items-center gap-1 font-semibold text-accent"
                                : "inline-flex items-center gap-1 text-ink"
                            }
                            title={stale ? "Past the expected clearing window" : undefined}
                          >
                            {stale && <Clock className="h-3.5 w-3.5" aria-hidden="true" />}
                            {days}d
                          </span>
                        )}
                      </TableCell>
                      {/* Recording a payment is the whole job of this screen, so
                          it stays a button on the row — burying it would cost a
                          click on every family. The rest were icon-only, which
                          meant guessing what an envelope did; in the menu they
                          get their words and a line naming the consequence.
                          See components/RowActions. */}
                      <TableCell className="sticky right-0 z-10 bg-card text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {e.payment_status === "paid" ? (
                            <span className="inline-flex items-center gap-1 text-sm text-success">
                              <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> Paid
                            </span>
                          ) : (
                            <Button
                              type="button"
                              size="sm"
                              variant="accent"
                              onClick={() => setRecordTarget(e)}
                            >
                              Record payment
                            </Button>
                          )}
                          <RowActions label={`Actions for ${e.payment_ref}`}>
                            {/* On a PAID row too: a record can read as settled
                                because the fee itself is wrong (a waiver or
                                discount that shouldn't have applied), and the
                                only way to chase that is to start here. */}
                            <RowAction
                              icon={Send}
                              label={
                                e.payment_status === "paid"
                                  ? "Correct the fee and ask for payment"
                                  : "Ask the family for payment"
                              }
                              description={
                                e.payment_status === "paid"
                                  ? "Fix a fee that was wrong — a waiver or discount that shouldn't have applied — and email the family what they now owe."
                                  : "Email this family a reminder of what they still owe, with the amount, reference and a Pay now link filled in."
                              }
                              onSelect={() => openRequest(e)}
                            />
                            {e.payment_status !== "paid" && (
                              <RowAction
                                icon={Mail}
                                label="Send to the account manager"
                                description="Email whoever checks the bank a link to this one payment, so they can confirm it came in."
                                disabled={notifyAm.isPending}
                                onSelect={() => {
                                  setNotifySent(null);
                                  setNotifyTarget(e);
                                }}
                              />
                            )}
                            <RowAction
                              icon={History}
                              label="Payment history"
                              description="Every payment recorded against this family, and where to remove one entered by mistake."
                              onSelect={() => setHistoryTarget(e)}
                            />
                          </RowActions>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Record payment (partial or full) */}
      <RecordPaymentDialog
        enrolment={recordTarget}
        onOpenChange={(open) => {
          if (!open) {
            setRecordTarget(null);
            record.reset();
          }
        }}
        onSubmit={(input) => {
          if (recordTarget) record.mutate({ ref: recordTarget.payment_ref, input });
        }}
        pending={record.isPending}
        error={record.error}
      />

      {/* Ask one family for what they still owe — and correct the fee when
          that is what is actually wrong. The words are the host's; the amount,
          reference and the Pay now link are filled in server-side from the
          record, so they cannot be got wrong here. */}
      <RequestPaymentDialog
        enrolment={requestTarget}
        family={
          requestTarget
            ? (familyOwing.get((requestTarget.parent_email ?? "").trim().toLowerCase()) ?? null)
            : null
        }
        onOpenChange={(open) => {
          if (!open) {
            setRequestTarget(null);
            requestPayment.reset();
            adjustFee.reset();
          }
        }}
        onSend={(input) => void sendRequest(input)}
        onSaveFee={(fee) => void saveFeeOnly(fee)}
        pending={requestPayment.isPending || adjustFee.isPending}
        error={requestPayment.error}
        feeError={adjustFee.error}
      />
      {/* Confirm before the envelope actually sends. Names the record, and
          says plainly who does — and does not — receive it. */}
      <Dialog
        open={notifyTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setNotifyTarget(null);
            notifyAm.reset();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Email the account manager?</DialogTitle>
            <DialogDescription>
              {notifyTarget ? (
                <>
                  This sends a sign-in link for{" "}
                  <strong className="text-ink">{notifyTarget.payment_ref}</strong> —{" "}
                  {notifyTarget.parent_name} ({notifyTarget.child_name}),{" "}
                  {formatAud(notifyTarget.amount_cents)} — so they can reconcile it.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <p className="rounded-card border border-border bg-paper p-3 text-sm text-ink">
            The parent is <strong>not</strong> emailed. Only the account manager receives
            this.
          </p>
          {notifyAm.isError && (
            <p
              className="rounded-card border border-border bg-paper p-3 text-sm text-ink"
              role="alert"
            >
              Couldn&apos;t send that email
              {notifyAm.error instanceof ApiError ? ` (error ${notifyAm.error.status})` : ""}.
              Please try again.
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setNotifyTarget(null)}
              disabled={notifyAm.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="accent"
              disabled={notifyAm.isPending}
              onClick={() => {
                if (notifyTarget) notifyAm.mutate(notifyTarget.id);
              }}
            >
              {notifyAm.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Sending…
                </>
              ) : (
                "Yes, send it"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment history */}
      <Dialog
        open={historyTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setHistoryTarget(null);
            voidPayment.reset();
            editPayment.reset();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Payment history{historyTarget ? ` — ${historyTarget.payment_ref}` : ""}
            </DialogTitle>
            <DialogDescription>
              {historyTarget
                ? `${historyTarget.parent_name} (${historyTarget.child_name})` +
                  (programName(historyTarget.program_id)
                    ? ` — ${programName(historyTarget.program_id)}`
                    : "")
                : null}
            </DialogDescription>
          </DialogHeader>
          {history.isLoading ? (
            <div className="flex items-center gap-2 py-6 text-muted">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading…
            </div>
          ) : history.data ? (
            <>
              <PaymentHistory
                payments={history.data.payments}
                events={history.data.events}
                onVoid={(p) => {
                  if (historyTarget) {
                    voidPayment.mutate({
                      ref: historyTarget.payment_ref,
                      paymentId: p.id,
                    });
                  }
                }}
                voidPendingId={voidPayment.isPending ? voidPayment.variables?.paymentId ?? null : null}
                onEdit={(paymentId, input) => {
                  if (historyTarget) {
                    editPayment.mutate({
                      ref: historyTarget.payment_ref,
                      paymentId,
                      input,
                    });
                  }
                }}
              />
              {(voidPayment.isError || editPayment.isError) && (
                <p
                  className="rounded-card border border-border bg-paper p-3 text-sm text-ink"
                  role="alert"
                >
                  Couldn&apos;t {voidPayment.isError ? "remove" : "correct"} that payment
                  {(() => {
                    const err = voidPayment.isError ? voidPayment.error : editPayment.error;
                    return err instanceof ApiError ? ` (error ${err.status})` : "";
                  })()}
                  . Please try again.
                </p>
              )}
              {editPayment.isPending && (
                <p className="flex items-center gap-2 text-sm text-muted">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Saving
                  correction…
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-muted">Couldn&apos;t load the history.</p>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
